import React, { useEffect, useState, useCallback } from 'react';
import { Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useT } from '../../i18n/LanguageContext';
import { schoolApi } from '../../api/school';
import { PageHeader, EmptyState, LoadingState, Card } from '../../components/school/ui';
import { ReportCardView } from '../../components/school/ReportCardView';
import { AttendanceHistory } from '../../components/school/AttendanceHistory';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';

export default function ParentPortalPage() {
  const { user } = useAuth();
  const { t } = useT();
  const orgId = user?.organization_id;
  const [parent, setParent] = useState(null);
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState('');
  const [exams, setExams] = useState([]);
  const [examId, setExamId] = useState('');
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noProfile, setNoProfile] = useState(false);

  const init = useCallback(async () => {
    try {
      setLoading(true);
      const me = await schoolApi.myParent();
      setParent(me);
      setChildren(me.children || []);
      if (me.children?.length) setChildId(me.children[0].id);
      const ex = await schoolApi.listExams(orgId);
      setExams(ex);
      if (ex.length) setExamId(ex[0].id);
    } catch (e) {
      if (e?.response?.status === 404) setNoProfile(true);
      else toast.error('Failed to load');
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { init(); }, [init]);

  useEffect(() => {
    if (!examId || !childId) { setCard(null); return; }
    schoolApi.getReportCard({ organization_id: orgId, exam_id: examId, student_id: childId }).then(setCard).catch(() => setCard(null));
  }, [examId, childId, orgId]);

  if (loading) return <div className="max-w-[1400px] mx-auto"><LoadingState rows={6} /></div>;
  if (noProfile) return <div className="max-w-[1400px] mx-auto"><Card><EmptyState icon={Users} title={t('portal.noProfile')} /></Card></div>;

  return (
    <div className="max-w-[1400px] mx-auto">
      <PageHeader title={t('portal.parentTitle')} subtitle={parent?.name} icon={Users}
        actions={
          <Select value={childId} onValueChange={setChildId}>
            <SelectTrigger className="w-48" data-testid="parent-child-select"><SelectValue placeholder="Child" /></SelectTrigger>
            <SelectContent>{children.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
        }
      />
      {children.length === 0 ? (
        <Card><EmptyState icon={Users} title={t('common.noData')} /></Card>
      ) : (
        <Tabs defaultValue="report" className="space-y-4">
          <TabsList data-testid="parent-portal-tabs">
            <TabsTrigger value="report" data-testid="parent-tab-report">{t('nav.reportCards')}</TabsTrigger>
            <TabsTrigger value="attendance" data-testid="parent-tab-attendance">{t('att.myTitle')}</TabsTrigger>
          </TabsList>
          <TabsContent value="report" className="space-y-4">
            <div className="flex justify-end">
              <Select value={examId} onValueChange={setExamId}>
                <SelectTrigger className="w-56" data-testid="parent-exam-select"><SelectValue placeholder={t('portal.selectExam')} /></SelectTrigger>
                <SelectContent>{exams.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {card ? <ReportCardView card={card} /> : <Card><EmptyState icon={Users} title={t('common.noData')} description={t('portal.selectExam')} /></Card>}
          </TabsContent>
          <TabsContent value="attendance">
            <AttendanceHistory studentId={childId} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
